import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { OrderQuery } from '@modules/order/state/order-query';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { ApplianceDetailsUtils } from './utils/appliance-details-utils';
import { ISectionVisibilityService } from '@shared/models/section-visibility';
import { ProcedureMap } from '@shared/models/procedure-map';

export interface ApplianceDetailsFieldsVisibility {
	applianceType: boolean;
	upperLowerCard: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApplianceDetailsVisibilityService implements ISectionVisibilityService<ApplianceDetailsFieldsVisibility> {
	// ── private ──────────────────────────────────────────────────────────────

	private readonly isSwoEnabled$: Observable<boolean> = this.softwareOptionsService
		.hasCompanySoftwareOption$(SoftwareOptions.EnableApplianceDetails)
		.pipe(shareReplay({ bufferSize: 1, refCount: true }));

	private readonly isProcedureAppliance$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => !!procedureMap && ApplianceDetailsUtils.isApplianceDetailsEnabled(procedureMap.ProcedureId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	// ── public ───────────────────────────────────────────────────────────────

	readonly fields$: Observable<ApplianceDetailsFieldsVisibility> = combineLatest([
		this.orderQuery.procedureMap$,
		this.isSwoEnabled$,
		this.rxTemplateService.template$
	]).pipe(
		map(([procedureMap, swoEnabled, template]) =>
			ApplianceDetailsVisibilityService.fieldsFromContext(procedureMap, swoEnabled, template)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly isSectionVisible$: Observable<boolean> = combineLatest([
		this.isProcedureAppliance$,
		this.fields$.pipe(map(f => ApplianceDetailsVisibilityService.isSectionNonEmpty(f)))
	]).pipe(
		map(([isAppliance, nonEmpty]) => isAppliance && nonEmpty),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	static fieldsFromContext(
		procedureMap: ProcedureMap | null,
		swoEnabled: boolean,
		template: Readonly<RxTemplate>
	): ApplianceDetailsFieldsVisibility {
		return {
			applianceType:
				swoEnabled && !!procedureMap && ApplianceDetailsUtils.isApplianceTypeVisible(procedureMap.ProcedureId, procedureMap.TypeId),
			upperLowerCard: swoEnabled && !template.ui.appliance?.items.upperLowerCard.hidden
		};
	}

	static isSectionNonEmpty(f: ApplianceDetailsFieldsVisibility): boolean {
		return f.applianceType || f.upperLowerCard;
	}

	constructor(
		private readonly orderQuery: OrderQuery,
		private readonly rxTemplateService: RxTemplateService,
		private readonly softwareOptionsService: SoftwareOptionsService
	) {}

	fieldsFor$(
		targetTemplate: Readonly<RxTemplate>,
		targetProcedureMap: ProcedureMap | null
	): Observable<ApplianceDetailsFieldsVisibility> {
		// No procedure map or non-appliance procedure → all fields hidden.
		if (!targetProcedureMap || !ApplianceDetailsUtils.isApplianceDetailsEnabled(targetProcedureMap.ProcedureId)) {
			return of({ applianceType: false, upperLowerCard: false });
		}

		return this.isSwoEnabled$.pipe(
			take(1),
			map(swoEnabled => ApplianceDetailsVisibilityService.fieldsFromContext(targetProcedureMap, swoEnabled, targetTemplate))
		);
	}
}
